import Layout from '../../components/Layout/Layout';
import { MaterialCategory, MaterialType, Usage } from '../../enum/Material';
import isAdminUser from '../../hoc/isAdminUser';
import './BackofficeMaterial.scss';
import { IBackofficeMaterialProps, IFormikBOM } from './IBackofficeMaterial';
import { Field, Form, Formik } from 'formik';
import Button from '../../../components/Button/Button';
import { useNavigate } from 'react-router-dom';
import { endpoint } from '..';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import { useTranslation } from 'react-i18next';
import { FormikImages } from './components/FormikImages';
import { FormikGeneralInfo } from './components/FormikGeneralInfo';
import { materialTypeOptions } from '../../MACRA_1/components/InputForm/Materials/MaterialDropdowns/MaterialTypeDropdown';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import { useState } from 'react';
import Spinner from '../../../components/Spinner/Spinner';
import { toastSuccess, warning } from '../../../components/Alert/Alert';
import { conversionLabel } from '../../conversion';
import { useAppSelector } from '../../../reduxService/hooks';
import { selectProjectInfo } from '../../store/projectInfo/projectInfoSlice';

const defaultMaterial = {
    usage: Usage.Base,
    type: MaterialType.ProtectionSystems,
    category: MaterialCategory.Other,
    creationDate: new Date(),
    updateDate: new Date(),
    isDeleted: false,
    isXties: false,
    isCastoro: false,
    isGeotextileFilter: false,
    status: true,
    color: '#004652',
    images: [],
    roughness: 0,
    allowShearStress: 0,
    infos: [{ languageCodeISO: 'en', name: '', description: '' }],
    isAvailableStilling: false,
    isAvailableSoil: false,
    isAllowedChangeCheckbox: false,
};

const BackofficeMaterialCreate = (props: IBackofficeMaterialProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const projectInfo = useAppSelector(selectProjectInfo);
    const initialValues: IFormikBOM = defaultMaterial;

    return (
        <Layout>
            <Formik
                initialValues={initialValues}
                onSubmit={async values => {
                    setLoading(true);
                    try {
                        const result = await BeMaccaferriStoreHelper.addMaterialAsync(values);
                        if (!!result.data && result.data) {
                            toastSuccess(t('bo_material_toast_success_created'));
                            navigate(endpoint.backoffice);
                        } else {
                            warning(t('bo_material_toast_error_created'));
                        }
                    } catch (err) {
                        warning(t('bo_material_toast_error_created'));
                    } finally {
                        setLoading(false);
                    }
                }}>
                {({ handleSubmit, values, handleChange }) => {
                    return (
                        <Form onSubmit={handleSubmit} className='overflow-auto scroll-no-width h-100'>
                            <div className='d-flex align-items-center gap-4 m-2 dropdown-check'>
                                <Dropdown
                                    title={t('bo_material_dd_typology')}
                                    name='type'
                                    className='d-flex align-items-center gap-2'
                                    value={values.type}
                                    options={materialTypeOptions()}
                                    onChange={handleChange}
                                />
                                <Checkbox title={t('bo_material_checkbox_status')} name='status' defaultChecked={values.status} onChange={handleChange} />
                                <Checkbox title={t('bo_material_checkbox_isGeotextileFilter')} name='isGeotextileFilter' defaultChecked={values.isGeotextileFilter} onChange={handleChange} />
                                <Checkbox title={t('bo_material_checkbox_isXties')} name='isXties' defaultChecked={values.isXties} onChange={handleChange} />
                                <Checkbox title={t('bo_material_checkbox_isCastoro')} name='isCastoro' defaultChecked={values.isCastoro} onChange={handleChange} />
                                <Checkbox title={t('bo_material_checkbox_isAvailableStillingBasin')} name='isAvailableStilling' defaultChecked={values.isAvailableStilling} onChange={handleChange} />
                                <Checkbox title={t('bo_material_checkbox_isAvailableSoil')} name='isAvailableSoil' defaultChecked={values.isAvailableSoil} onChange={handleChange} />
                                <Checkbox
                                    title={t('bo_material_checkbox_isAllowedChangeCheckbox')}
                                    name='isAllowedChangeCheckbox'
                                    defaultChecked={values.isAllowedChangeCheckbox}
                                    onChange={handleChange}
                                />
                                <Input
                                    type='color'
                                    title={t('bo_material_input_color')}
                                    name='color'
                                    className='d-flex align-items-center gap-2'
                                    classNameInput='colored-box'
                                    value={values.color}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className='d-flex justify-content-center gap-4 '>
                                <FormikGeneralInfo />
                                <div className='w-25 d-flex flex-column gap-2'>
                                    <label className='py-3'>{t('bo_material_titles_pd')}</label>
                                    <div className='inputPhysicalData'>
                                        <label htmlFor='allowShearStress'>
                                            {t('bo_material_input_shearstress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                                        </label>
                                        <Field
                                            id='allowShearStress'
                                            type='number'
                                            step={0.01}
                                            name='allowShearStress'
                                            className='d-flex flex-column'
                                            defaultValue={values.allowShearStress}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor='roughness'> {t('bo_material_input_roughness')}</label>
                                        <Field type='number' step={0.0001} name='roughness' id='roughness' className='d-flex flex-column' defaultValue={values.roughness} onChange={handleChange} />
                                    </div>
                                </div>
                                <FormikImages />
                            </div>
                            <div className='d-flex justify-content-end gap-3 m-5'>
                                <Button
                                    type='button'
                                    classNameBtn='btn-color-inverted'
                                    onClick={() => {
                                        navigate(endpoint.backoffice);
                                    }}>
                                    {t('bo_material_cancel_btn')}
                                </Button>
                                {loading ? <Spinner /> : <Button type='submit'>{t('bo_material_save_btn')}</Button>}
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </Layout>
    );
};

export default isAdminUser(BackofficeMaterialCreate);
